import { useState, useEffect } from 'react';

import CustomModal from '@/components/CustomModal';
import DatePicker from '@/components/DatePicker';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { useFormatText } from '@/hooks/useFormatText';
import { getTeacherClassDropdown } from '@/service/classService';

export interface ILessonFilters {
  class: string | string[];
  assignedDate: number | null;
  dueDate: number | null;
  status: string;
}

import { LESSON_STATUS_OPTIONS } from '@/constants/constant';
import { IClassDropdownItem } from '@/types';
import { convertToTimestamp } from '@/utils';

interface IFilterModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onApplyFilters?: (filters: ILessonFilters) => void;
  initialFilters: ILessonFilters | null;
}

const FilterModal = ({
  isOpen,
  setIsOpen,
  onApplyFilters,
  initialFilters,
}: IFilterModalProps) => {
  const { formatText } = useFormatText();

  const [classValue, setClassValue] = useState<string | string[]>(
    initialFilters?.class || []
  );
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [assignedDate, setAssignedDate] = useState<string | null>(
    initialFilters?.assignedDate
      ? new Date(initialFilters.assignedDate * 1000).toISOString()
      : null
  );
  const [dueDate, setDueDate] = useState<string | null>(
    initialFilters?.dueDate
      ? new Date(initialFilters.dueDate * 1000).toISOString()
      : null
  );
  const [status, setStatus] = useState<string>(initialFilters?.status || '');

  useEffect(() => {
    const fetchClassOptions = async () => {
      try {
        const res = await getTeacherClassDropdown(true);
        if (res?.data) {
          const options = (res.data as IClassDropdownItem[]).map((item) => ({
            value: item.id,
            label: item.name,
          }));
          setClassOptions(options);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchClassOptions();
  }, []);

  const handleClearFilters = () => {
    setClassValue([]);
    setAssignedDate(null);
    setDueDate(null);
    setStatus('');
    onApplyFilters?.({
      class: [],
      assignedDate: null,
      dueDate: null,
      status: '',
    });
    setIsOpen(false);
  };

  const handleApplyFilters = () => {
    onApplyFilters?.({
      class: classValue,
      assignedDate: convertToTimestamp(assignedDate),
      dueDate: convertToTimestamp(dueDate),
      status,
    });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      position="right"
      isHeader
      heading={formatText('common.filter')}
      isFooter
      cancelButtonProps={{
        label: formatText('common.clear-filters'),
        onClick: handleClearFilters,
        variant: 'link',
      }}
      confirmButtonProps={{
        label: formatText('common.show-results'),
        onClick: handleApplyFilters,
        variant: 'primary',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        <Select
          name={formatText('common.class')}
          options={classOptions}
          selected={classValue}
          onChange={setClassValue}
          placeholder="Select Class"
          className="w-full"
          isMulti
        />

        <DatePicker
          value={assignedDate}
          onChange={(date) => setAssignedDate(date)}
          placeholder="MM/DD/YYYY"
          containerClassName="w-full"
          label={formatText('lessons.columns.assigned-date')}
        />

        <DatePicker
          value={dueDate}
          onChange={(date) => setDueDate(date)}
          placeholder="MM/DD/YYYY"
          containerClassName="w-full"
          label={formatText('lessons.columns.due-date')}
        />

        <RadioButtonGroup
          name="status"
          label={formatText('common.status-label')}
          options={LESSON_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          direction="row"
          gap="gap-8"
          formatLabel={formatText}
        />
      </div>
    </CustomModal>
  );
};

export default FilterModal;
